import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationsData } from '../../mock';
import { useAuth } from '../../contexts/AuthContext';
import {
  Search,
  Bell,
  Sun,
  Moon,
  ChevronDown,
  LogOut,
  User,
  Settings,
  Menu
} from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Avatar, AvatarFallback } from '../ui/avatar';

interface HeaderProps {
  isDark: boolean;
  setIsDark: (value: boolean) => void;
  sidebarCollapsed: boolean;
  onMobileMenuToggle: () => void;
}

export default function Header({ isDark, setIsDark, sidebarCollapsed, onMobileMenuToggle }: HeaderProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const unreadCount = notificationsData.filter(n => !n.read).length;
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const displayName = user?.firstName && user?.lastName 
    ? `${user.firstName} ${user.lastName}`
    : user?.username || 'Admin';
  
  const displayEmail = user?.email || 'admin@example.com';

  return (
    <header className={cn(
      'h-16 fixed top-0 right-0 z-30 flex items-center justify-between px-6 transition-all duration-300',
      sidebarCollapsed ? 'left-20' : 'left-64',
      'lg:left-20 lg:left-64',
      isDark ? 'bg-[#0F172A]/95 backdrop-blur-sm border-b border-slate-700' : 'bg-white/95 backdrop-blur-sm border-b border-slate-200'
    )}
    style={{ left: sidebarCollapsed ? '80px' : '256px' }}
    >
      {/* Mobile menu button */}
      <button
        onClick={onMobileMenuToggle}
        className={cn(
          'lg:hidden p-2 rounded-lg mr-2',
          isDark ? 'text-slate-400 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-100'
        )}
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Search */}
      <div className="flex-1 max-w-md">
        <div className="relative">
          <Search className={cn(
            'absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4',
            isDark ? 'text-slate-500' : 'text-slate-400'
          )} />
          <Input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={cn(
              'pl-10 h-10 rounded-xl border-0',
              isDark 
                ? 'bg-slate-800 text-white placeholder:text-slate-500 focus:ring-[#0EA5A4]' 
                : 'bg-slate-100 text-slate-900 placeholder:text-slate-400 focus:ring-[#0EA5A4]'
            )}
          />
        </div>
      </div>

      {/* Right section */}
      <div className="flex items-center gap-2">
        {/* Theme toggle */}
        <button
          onClick={() => setIsDark(!isDark)}
          className={cn(
            'p-2.5 rounded-xl transition-all duration-200',
            isDark 
              ? 'text-slate-400 hover:bg-slate-800 hover:text-white' 
              : 'text-slate-500 hover:bg-slate-100 hover:text-[#0F172A]'
          )}
        >
          {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>

        {/* Notifications */}
        <Popover>
          <PopoverTrigger asChild>
            <button className={cn(
              'p-2.5 rounded-xl transition-all duration-200 relative',
              isDark 
                ? 'text-slate-400 hover:bg-slate-800 hover:text-white' 
                : 'text-slate-500 hover:bg-slate-100 hover:text-[#0F172A]'
            )}>
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center font-medium">
                  {unreadCount}
                </span>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent 
            className={cn(
              'w-80 p-0 rounded-xl',
              isDark ? 'bg-[#1E293B] border-slate-700' : 'bg-white border-slate-200'
            )}
            align="end"
          >
            <div className={cn(
              'px-4 py-3 border-b',
              isDark ? 'border-slate-700' : 'border-slate-200'
            )}>
              <h3 className={cn(
                'font-semibold',
                isDark ? 'text-white' : 'text-[#0F172A]'
              )}>Notifications</h3>
            </div>
            <div className="max-h-72 overflow-y-auto">
              {notificationsData.map((notification) => (
                <div
                  key={notification.id}
                  className={cn(
                    'px-4 py-3 border-b last:border-b-0 transition-colors',
                    isDark ? 'border-slate-700 hover:bg-slate-800' : 'border-slate-100 hover:bg-slate-50',
                    !notification.read && (isDark ? 'bg-slate-800/50' : 'bg-[#0EA5A4]/5')
                  )}
                >
                  <p className={cn(
                    'text-sm font-medium',
                    isDark ? 'text-white' : 'text-[#0F172A]'
                  )}>{notification.title}</p>
                  <p className={cn(
                    'text-xs mt-0.5',
                    isDark ? 'text-slate-400' : 'text-slate-500'
                  )}>{notification.message}</p>
                  <p className={cn(
                    'text-xs mt-1',
                    isDark ? 'text-slate-500' : 'text-slate-400'
                  )}>{notification.time}</p>
                </div>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        {/* User dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className={cn(
              'flex items-center gap-2 px-2 py-1.5 rounded-xl transition-all duration-200',
              isDark 
                ? 'hover:bg-slate-800' 
                : 'hover:bg-slate-100'
            )}>
              <Avatar className="w-8 h-8">
                <AvatarFallback className="bg-gradient-to-br from-[#0EA5A4] to-[#0EA5A4]/70 text-white text-sm font-medium">
                  {displayName.split(' ').map(n => n[0]).join('').toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="hidden sm:block text-left">
                <p className={cn(
                  'text-sm font-medium leading-none',
                  isDark ? 'text-white' : 'text-[#0F172A]'
                )}>{displayName}</p>
                <p className={cn(
                  'text-xs mt-0.5',
                  isDark ? 'text-slate-400' : 'text-slate-500'
                )}>Administrator</p>
              </div>
              <ChevronDown className={cn(
                'w-4 h-4 hidden sm:block',
                isDark ? 'text-slate-400' : 'text-slate-500'
              )} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent 
            className={cn(
              'w-56 rounded-xl',
              isDark ? 'bg-[#1E293B] border-slate-700' : 'bg-white border-slate-200'
            )}
            align="end"
          >
            <div className={cn(
              'px-3 py-2 border-b',
              isDark ? 'border-slate-700' : 'border-slate-200'
            )}>
              <p className={cn(
                'text-sm font-medium',
                isDark ? 'text-white' : 'text-[#0F172A]'
              )}>{displayName}</p>
              <p className={cn(
                'text-xs',
                isDark ? 'text-slate-400' : 'text-slate-500'
              )}>{displayEmail}</p>
            </div>
            <DropdownMenuItem className={cn(
              'cursor-pointer rounded-lg mx-1 my-1',
              isDark ? 'text-slate-300 focus:bg-slate-800 focus:text-white' : 'text-slate-600 focus:bg-slate-100'
            )}>
              <User className="w-4 h-4 mr-2" />
              Profile
            </DropdownMenuItem>
            <DropdownMenuItem className={cn(
              'cursor-pointer rounded-lg mx-1',
              isDark ? 'text-slate-300 focus:bg-slate-800 focus:text-white' : 'text-slate-600 focus:bg-slate-100'
            )}>
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator className={isDark ? 'bg-slate-700' : 'bg-slate-200'} />
            <DropdownMenuItem 
              onClick={handleLogout}
              className={cn(
                'cursor-pointer rounded-lg mx-1 my-1 text-red-500 focus:text-red-500',
                isDark ? 'focus:bg-slate-800' : 'focus:bg-red-50'
              )}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

